import React, { useCallback, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import useIndexOtaChannel from '../../../services/ota-channel/hooks/useIndexOtaChannel';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import { useResetDefaultColor } from '../../../services/ota-channel/hooks/useResetDefaultColor';
import { toast } from 'sonner';
import { useAuth } from '../../../auth/context/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import OtaFieldColorComponent from './OtaFieldColorComponent';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';

const OtaChannelTable: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [limit, _setLimit] = useState<number>(10);
    const { data: otaData, isSuccess, isError, isLoading, isFetching } = useIndexOtaChannel({
        page: page,
        limit: limit,
    });

    return (
        <>
            <div className='mt-5'>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                    <h3 className="mb-3 mb-md-0">Ota Channel</h3>
                    <ButtonResetDefaultColor />
                </div>
                <div className="d-none d-md-block">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Code</th>
                                <th>Primary Color</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isError && <tr><td colSpan={3} className='text-center'>Failed to load data.</td></tr>}
                            {isLoading && <tr><td colSpan={3} className='text-center'>Loading...</td></tr>}
                            {isSuccess && !isLoading && otaData.data.length === 0 && <tr><td colSpan={3} className='text-center'>No data available, <span className='text-danger'>press reset default color to reset all ota color</span></td></tr>}
                            {isSuccess && !isLoading && otaData.data.length > 0 && otaData.data.map((ota, idx) => (
                                <tr key={idx}>
                                    <td>{ota.name}</td>
                                    <td>{ota.code}</td>
                                    <td><ReadOnlyGuard><OtaFieldColorComponent ota={ota} /></ReadOnlyGuard></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-md-none">
                    {isError && <div className="text-center my-4">Failed to load data.</div>}
                    {isLoading && <div className="text-center my-4">Loading...</div>}
                    {isSuccess && !isLoading && otaData.data.length === 0 && <div className='text-center my-4'>No data available, <span className='text-danger'>press reset default color to reset all ota color</span></div>}
                    {isSuccess && !isLoading && otaData.data.length > 0 && otaData.data.map((ota, idx) => (
                        <div key={idx} className="card mb-3">
                            <div className="card-body">
                                <h5 className="card-title"><strong>{ota.name}</strong></h5>
                                <p className="card-text mb-1"><strong>Code:</strong> {ota.code}</p>
                                <p className="card-text mb-1"><strong>Primary Color:</strong> <ReadOnlyGuard><OtaFieldColorComponent ota={ota} /></ReadOnlyGuard></p>
                            </div>
                        </div>
                    ))}
                </div>
                
                {isSuccess && !isFetching && otaData.pagination && (
                    <div className='mt-3'>
                        <PaginationComponent
                            totalPages={otaData.pagination.last_page}
                            currentPage={otaData.pagination.current_page}
                            onPageChange={(page) => setPage(page)}
                        />
                    </div>
                )}
            </div>
        </>
    )
}

export default OtaChannelTable

const ButtonResetDefaultColor: React.FC = () => {
    const { user } = useAuth();
    const { mutate, isPending } = useResetDefaultColor();

    const queryClient = useQueryClient();

    const onReset = useCallback(() => {
        toast.loading('Resetting default color...', { id: 'reset-default-color' });
        if (!user) return;
        mutate({
            property_id: user.active_property_id
        }, {
            onSuccess: async () => {

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['ota'].includes(key as string))
                });

                toast.success('Reset default color successfully', { id: 'reset-default-color' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        toast.error('Failed to reset default color ' + Object.values(validationErrors).join(', '), { id: 'reset-default-color' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to reset default color', { id: 'reset-default-color' });
                }
            }
        })
    }, [mutate]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-reset">Press this button to reset all ota color to default</Tooltip>}
        >
            <ReadOnlyGuard>
                <button className="btn btn-warning" onClick={() => onReset()} disabled={isPending}>Reset Default Color</button>
            </ReadOnlyGuard>
        </OverlayTrigger>
    )
}